import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  API_LIST_TICKETS,
  API_ASSIGN_TICKET,
  API_UPDATE_TICKET_STATUS,
  API_UPDATE_TICKET_PRIORITY,
  API_TICKET_STATS,
} from "../api/ticket.service";

export const fetchTickets = createAsyncThunk(
  "tickets/fetchTickets",
  async (payload, thunkAPI) => {
    try {
      const data = await API_LIST_TICKETS(payload);
      return data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchTicketStats = createAsyncThunk(
  "tickets/fetchTicketStats",
  async (payload, thunkAPI) => {
    try {
      const data = await API_TICKET_STATS(payload);
      return data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const assignTicket = createAsyncThunk(
  "tickets/assignTicket",
  async ({ ticketId, assignedTo }, thunkAPI) => {
    try {
      const data = await API_ASSIGN_TICKET(ticketId, assignedTo);
      return { data, ticketId, assignedTo };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateTicketStatus = createAsyncThunk(
  "tickets/updateTicketStatus",
  async ({ ticketId, status }, thunkAPI) => {
    try {
      const data = await API_UPDATE_TICKET_STATUS(ticketId, status);
      return { data, ticketId, status };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateTicketPriority = createAsyncThunk(
  "tickets/updateTicketPriority",
  async ({ ticketId, priority }, thunkAPI) => {
    try {
      const data = await API_UPDATE_TICKET_PRIORITY(ticketId, priority);
      return { data, ticketId, priority };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

const initialState = {
  list: { rows: [], count: 0 },
  stats: {
    totalTickets: 0,
    openTickets: 0,
    inProgressTickets: 0,
    resolvedTickets: 0,
    closedTickets: 0,
    slaBreachedTickets: 0,
    avgFirstResponseTimeMinutes: 0,
    avgResolutionTimeMinutes: 0,
  },
  loading: { list: false, stats: false, update: false },
  error: { list: null, stats: null, update: null },
};

const updateRowById = (state, ticketId, updater) => {
  const index = state.list.rows.findIndex((row) => row.id === ticketId);
  if (index >= 0) {
    state.list.rows[index] = {
      ...state.list.rows[index],
      ...updater,
    };
  }
};

const ticketSlice = createSlice({
  name: "tickets",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchTickets.pending, (state) => {
      state.loading.list = true;
      state.error.list = null;
    });
    builder.addCase(fetchTickets.fulfilled, (state, action) => {
      state.loading.list = false;
      state.list = action.payload?.data || { rows: [], count: 0 };
    });
    builder.addCase(fetchTickets.rejected, (state, action) => {
      state.loading.list = false;
      state.error.list = action.payload || "Failed to load tickets";
    });

    builder.addCase(fetchTicketStats.pending, (state) => {
      state.loading.stats = true;
      state.error.stats = null;
    });
    builder.addCase(fetchTicketStats.fulfilled, (state, action) => {
      state.loading.stats = false;
      state.stats = action.payload?.data || initialState.stats;
    });
    builder.addCase(fetchTicketStats.rejected, (state, action) => {
      state.loading.stats = false;
      state.error.stats = action.payload || "Failed to load stats";
    });

    builder.addCase(assignTicket.pending, (state) => {
      state.loading.update = true;
      state.error.update = null;
    });
    builder.addCase(assignTicket.fulfilled, (state, action) => {
      state.loading.update = false;
      updateRowById(state, action.payload.ticketId, {
        assignedTo: action.payload.assignedTo,
      });
    });
    builder.addCase(assignTicket.rejected, (state, action) => {
      state.loading.update = false;
      state.error.update = action.payload || "Failed to assign ticket";
    });

    builder.addCase(updateTicketStatus.pending, (state) => {
      state.loading.update = true;
      state.error.update = null;
    });
    builder.addCase(updateTicketStatus.fulfilled, (state, action) => {
      state.loading.update = false;
      updateRowById(state, action.payload.ticketId, {
        status: action.payload.status,
      });
    });
    builder.addCase(updateTicketStatus.rejected, (state, action) => {
      state.loading.update = false;
      state.error.update = action.payload || "Failed to update status";
    });

    builder.addCase(updateTicketPriority.pending, (state) => {
      state.loading.update = true;
      state.error.update = null;
    });
    builder.addCase(updateTicketPriority.fulfilled, (state, action) => {
      state.loading.update = false;
      updateRowById(state, action.payload.ticketId, {
        priority: action.payload.priority,
      });
    });
    builder.addCase(updateTicketPriority.rejected, (state, action) => {
      state.loading.update = false;
      state.error.update = action.payload || "Failed to update priority";
    });
  },
});

export default ticketSlice.reducer;
